import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { validateResetToken, resetPassword as apiResetPassword } from '../services/api';
import './AuthPage.css';

/* ── Password strength helper (mirrors HTML exactly) ── */
function getStrength(password) {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const levels = [
    { text: 'Very Weak', color: '#e74c3c' },
    { text: 'Weak', color: '#e74c3c' },
    { text: 'Fair', color: '#f39c12' },
    { text: 'Good', color: '#3498db' },
    { text: 'Strong', color: '#27ae60' },
  ];
  return { score, checks, ...levels[score] };
}

const REQUIREMENTS = [
  { key: 'length', label: 'At least 8 characters' },
  { key: 'uppercase', label: 'One uppercase letter (A-Z)' },
  { key: 'lowercase', label: 'One lowercase letter (a-z)' },
  { key: 'number', label: 'One number (0-9)' },
];

function ResetPassword() {
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [strength, setStrength] = useState(null);

  /*
   * On mount: extract token from URL, validate it.
   * navigate is stable across renders so it is safe as a dependency.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';

    if (!t) {
      // HTML behaviour: show error, redirect home after 3 seconds
      setTokenInvalid(true);
      setStatus({ type: 'error', text: '❌ Invalid reset link. No token found.' });
      const timer = setTimeout(() => navigate('/'), 3000);
      return () => clearTimeout(timer);
    }

    setToken(t);

    // Validate the token against the backend
    validateResetToken(t).catch((err) => {
      setTokenInvalid(true);
      setStatus({
        type: 'error',
        text: `❌ ${err.message || 'Reset link is invalid or has expired.'}`,
      });
    });

    // navigate is intentionally the only dep; we only run this on mount
  }, [navigate]);

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    setStrength(val ? getStrength(val) : null);
  };

  const validate = () => {
    if (newPassword.length < 8) {
      setStatus({ type: 'error', text: '❌ Password must be at least 8 characters long' });
      return false;
    }
    if (newPassword !== confirmPassword) {
      setStatus({ type: 'error', text: '❌ Passwords do not match' });
      return false;
    }
    const s = getStrength(newPassword);
    if (!s.checks.length || !s.checks.uppercase || !s.checks.lowercase || !s.checks.number) {
      setStatus({ type: 'error', text: '❌ Password must include uppercase, lowercase, and a number' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setStatus({ type: '', text: '' });

    try {
      await apiResetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setStatus({
        type: 'error',
        text: `❌ ${err.message || 'Failed to reset password. Please try again.'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ── Success view ── */
  if (success) {
    return (
      <div className="auth-bg">
        <div className="auth-card">
          <div className="success-view">
            <div className="success-icon">✅</div>
            <h2>Password Reset Successfully!</h2>
            <p>
              Your password has been updated. You can now login to the Ab Meri Baari app
              with your new password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-bg">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/512-app-icon.png" alt="Ab Meri Baari" className="auth-app-icon" />
          <div className="auth-logo">Ab Meri Baari</div>
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">Create a new password for your account</p>
        </div>

        {status.text && (
          <div className={`auth-message ${status.type}`}>{status.text}</div>
        )}

        {/* Expired / invalid token box — mirrors HTML expired-link-message */}
        {tokenInvalid && (
          <div className="expired-box">
            <h3>🔗 Link No Longer Valid</h3>
            <p>
              This password reset link has already been used or has expired.
              Links are valid for 1 hour.
            </p>
            <Link
              to="/auth/forgot-password"
              className="auth-btn"
              style={{ display: 'inline-block', textAlign: 'center', marginTop: '12px' }}
            >
              Request New Link
            </Link>
          </div>
        )}

        {!tokenInvalid && (
          <form onSubmit={handleSubmit}>
            {/* ── New Password ── */}
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={handlePasswordChange}
                required
              />

              {/* Strength bar */}
              {strength && (
                <>
                  <div className="strength-bar-wrap">
                    <div
                      className="strength-bar-fill"
                      style={{
                        width: `${(strength.score / 4) * 100}%`,
                        backgroundColor: strength.color,
                      }}
                    />
                  </div>
                  <div className="strength-text" style={{ color: strength.color }}>
                    {strength.text}
                  </div>
                </>
              )}

              {/* Requirements checklist */}
              <div className="requirements">
                {REQUIREMENTS.map((r) => (
                  <div
                    key={r.key}
                    className={`requirement${strength && strength.checks[r.key] ? ' met' : ''}`}
                  >
                    <span className="check-icon">✓</span>
                    {r.label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Confirm Password ── */}
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? <><span className="spinner" />Resetting password...</> : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>Remember your password? Try to sign in into the app</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
